/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import { NavLink, useSearchParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";

export const LEGACY_HOTEL_SETTINGS_PERMISSIONS = Object.freeze([
  "hotel_settings.view_customer_settings",
  "hotel_settings.view_stay_settings",
  "hotel_settings.view_reservation_flow",
  "hotel_settings.view_required_fields",
  "hotel_settings.view_required_roles",
  "hotel_settings.view_revenue_settings",
]);

export function resolveNavbarSettingsTargets(currentUser, businessUnitId) {
  const permissions = currentUser?.permissions || {};
  const hasLegacySettings = LEGACY_HOTEL_SETTINGS_PERMISSIONS.some(
    (permission) => permissions[permission] === true
  );
  const hasSettingsBypass = permissions.access_settings === true;
  const canViewLegalEntity =
    Number(currentUser?.id) === 1 ||
    hasSettingsBypass ||
    permissions["finance.settings.view"] === true ||
    permissions["finance.settings.manage_legal_entity"] === true ||
    permissions["finance.settings.manage_taxes"] === true;
  const businessUnitQuery = `?business_unit_id=${encodeURIComponent(String(businessUnitId))}`;

  return {
    legacy: hasLegacySettings || hasSettingsBypass
      ? `/settings/reservation${businessUnitQuery}`
      : null,
    legalEntity: canViewLegalEntity
      ? `/settings/legal-entity${businessUnitQuery}`
      : null,
  };
}

function Navbar() {
  const currentUser = JSON.parse(localStorage.getItem("palantir_user"));
  const permissions = currentUser?.permissions || {};

  const [searchParams] = useSearchParams();
  const businessUnitId =
    searchParams.get("business_unit_id") ||
    currentUser?.active_business_unit_id ||
    "1";
  const [businessUnitName, setBusinessUnitName] = useState("");
  const settingsTargets = resolveNavbarSettingsTargets(currentUser, businessUnitId);

  useEffect(() => {
    const loadBusinessUnitName = async () => {
      for (let i = 0; i < 10; i++) {
        const token = localStorage.getItem("palantir_token");
        if (token) break;
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      try {
        const res = await axios.get(`/api/business-units/${businessUnitId}`);
        if (res.data?.name) {
          setBusinessUnitName(res.data.name);
        }
      } catch (err) {
        console.warn("❌ Error al cargar unidad de negocio:", err);
      }
    };

    if (businessUnitId) {
      loadBusinessUnitName();
    }
  }, [businessUnitId, localStorage.getItem("palantir_token")]);

  // 🎨 Estilos (recuperando el alto original)
  const linkStyle = {
    color: "#fff",
    fontWeight: "bold",
    textDecoration: "none",
    padding: "10px 18px",
    borderRadius: "6px",
    fontSize: "1rem",
    transition: "background 0.3s ease, color 0.3s ease",
  };

  const activeLinkStyle = {
    background: "#000",
    color: "#fff",
  };

  return (
    <nav
      className="navbar navbar-expand-lg navbar-dark fixed-top"
      style={{
        background: "linear-gradient(to right, #00C9A7, #5B2C6F, #000)",
        padding: "10px 0", // mismo alto vertical que antes
        width: "98.7%",
        minHeight: "56px",
      }}
    >
      <div className="container-fluid">
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav gap-4 align-items-center">
            {(permissions["reservations.view_list"] ||
              permissions["reservations.create"] ||
              permissions["reservations.view_availability"]) && (
              <li className="nav-item">
                <NavLink
                  to={`/reservations/list?business_unit_id=${businessUnitId}`}
                  style={({ isActive }) =>
                    isActive ? { ...linkStyle, ...activeLinkStyle } : linkStyle
                  }
                >
                  Reservations
                </NavLink>
              </li>
            )}

            {(permissions["front_desk.view_check_in"] ||
              permissions["front_desk.view_check_out"]) && (
              <li className="nav-item">
                <NavLink
                  to={`/front-desk/check-ins?business_unit_id=${businessUnitId}`}
                  style={({ isActive }) =>
                    isActive ? { ...linkStyle, ...activeLinkStyle } : linkStyle
                  }
                >
                  Front Desk
                </NavLink>
              </li>
            )}

            {(
              Number(currentUser?.id) === 1 ||
              permissions["rooms.view_matrix"] ||
              permissions["rooms.view_assignment"]
            ) && (
              <li className="nav-item">
                <NavLink
                  to={`/rooms-management/matrix?business_unit_id=${businessUnitId}`}
                  style={({ isActive }) =>
                    isActive ? { ...linkStyle, ...activeLinkStyle } : linkStyle
                  }
                >
                  Rooms Management
                </NavLink>
              </li>
            )}


            {permissions["revenue.view_room_rates"] && (
              <li className="nav-item">
                <NavLink
                  to={`/revenue-management-container/management?business_unit_id=${businessUnitId}`}
                  style={({ isActive }) =>
                    isActive ? { ...linkStyle, ...activeLinkStyle } : linkStyle
                  }
                >
                  Revenue Management
                </NavLink>

              </li>
            )}

            {settingsTargets.legacy && (
              <li className="nav-item">
                <NavLink
                  to={settingsTargets.legacy}
                  style={({ isActive }) =>
                    isActive ? { ...linkStyle, ...activeLinkStyle } : linkStyle
                  }
                >
                  Settings
                </NavLink>
              </li>
            )}
            {settingsTargets.legalEntity && (
              <li className="nav-item">
                <NavLink
                  to={settingsTargets.legalEntity}
                  style={({ isActive }) =>
                    isActive ? { ...linkStyle, ...activeLinkStyle } : linkStyle
                  }
                >
                  Hotel &amp; Legal
                </NavLink>
              </li>
            )}
            {(Number(currentUser?.id) === 1 || permissions["hotel_settings.view_users_tab"]) && (
              <li className="nav-item">
                <NavLink
                  to={`/settings/users?business_unit_id=${businessUnitId}`}
                  style={({ isActive }) =>
                    isActive ? { ...linkStyle, ...activeLinkStyle } : linkStyle
                  }
                >
                  Users &amp; Roles
                </NavLink>
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* 🔹 Panel lateral derecho */}
      <div
        style={{
          position: "absolute",
          top: "10px",
          right: "20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "2px",
        }}
      >
        {businessUnitName && (
          <div
            style={{
              color: "#fff",
              fontSize: "0.8rem",
              fontWeight: "bold",
              maxWidth: "250px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={businessUnitName}
          >
            {businessUnitName}
          </div>
        )}

        <button
          onClick={() => {
            localStorage.removeItem("palantir_token");
            localStorage.removeItem("palantir_user");
            window.location.href = "/";
          }}
          style={{
            background: "transparent",
            border: "none",
            color: "#fff",
            fontSize: "0.8rem",
            cursor: "pointer",
            opacity: 0.6,
          }}
          title="Logout"
        >
          Logout
        </button>
      </div>
    </nav>
  );
}

export default Navbar;
